import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ConfigProvider } from "@/contexts/ConfigContext";
import AppLayout from "@/components/AppLayout";
import ProtectedRoute from "@/components/ProtectedRoute";
import Landing from "@/pages/Landing";
import DelegarLanding from "@/pages/DelegarLanding";
import DelegarWhatsappArticle from "@/pages/DelegarWhatsappArticle";
import EditorialArticle from "@/pages/EditorialArticle";
import ChecklistGracias from "@/pages/ChecklistGracias";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import RegisterChooser from "@/pages/RegisterChooser";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import Delegations from "@/pages/Delegations";
import DelegationCreate from "@/pages/DelegationCreate";
import DelegationDetail from "@/pages/DelegationDetail";
import DelegationEdit from "@/pages/DelegationEdit";
import AsistenteIA from "@/pages/AsistenteIA";
import MisConsultasIA from "@/pages/MisConsultasIA";
import BibliotecaJuridica from "@/pages/BibliotecaJuridica";
import Mediators from "@/pages/Mediators";
import Lawyers from "@/pages/Lawyers";
import MediatorDetail from "@/pages/MediatorDetail";
import LawyerDetail from "@/pages/LawyerDetail";
import Blog from "@/pages/Blog";
import Profile from "@/pages/Profile";
import AdminVerifications from "@/pages/AdminVerifications";
import AdminGestionProfesionales from "@/pages/AdminGestionProfesionales";
import AdminConsolaMediaciones from "@/pages/AdminConsolaMediaciones";
import AdminMarketRates from "@/pages/AdminMarketRates";
import AdminRiskCases from "@/pages/AdminRiskCases";
import AdminCRM from "@/pages/AdminCRM";
import AdminAudit from "@/pages/AdminAudit";
import AdminAccounting from "@/pages/AdminAccounting";
import AdminUsersMaintenance from "@/pages/AdminUsersMaintenance";
import AdminPlanPayments from "@/pages/AdminPlanPayments";
import AdminManualTransfers from "@/pages/AdminManualTransfers";
import AdminBusinessConfig from "@/pages/AdminBusinessConfig";
import Inscripcion from "@/pages/Inscripcion";
import EmbajadorPionero from "@/pages/EmbajadorPionero";
import Invitar from "@/pages/Invitar";
import Guia from "@/pages/Guia";
import Comunidad from "@/pages/Comunidad";
import MediacionFamiliar from "@/pages/MediacionFamiliar";
import MediationRequestDetail from "@/pages/MediationRequestDetail";
import ParaInversionistas from "@/pages/ParaInversionistas";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Mediations from "@/pages/Mediations";
import MediationDetail from "@/pages/MediationDetail";
import MisMediaciones from "@/pages/MisMediaciones";
import Membership from "@/pages/Membership";
import MembershipCheckout from "@/pages/MembershipCheckout";
import MembershipReserve from "@/pages/MembershipReserve";
import Terms from "@/pages/Terms";
import PreviewSonidos from "@/pages/PreviewSonidos";
import { QAList, QANew, QADetail } from "@/pages/QA";
import { Toaster } from "sonner";
import { useEffect } from "react";
import InstallPrompt from "@/components/InstallPrompt";
import WhatsAppFloating from "@/components/WhatsAppFloating";
import { unregisterServiceWorkers } from "@/lib/serviceWorker";

const SITE_URL = "https://colegalink.cl";
const DEFAULT_IMAGE = SITE_URL + "/og-delegar.png";

const STATIC_SEO = {
  "/": {
    title: "Colega Link | Plataforma para abogados y mediadores en Chile",
    description: "Plataforma chilena para delegaciones jurídicas entre abogados verificados, mediación familiar y tecnología para una práctica profesional más eficiente.",
  },
  "/planes": {
    title: "Planes para abogados | Colega Link",
    description: "Conoce los planes de Colega Link, incluido el Plan Pionero Fundador, sus beneficios Premium, Colega IA y condiciones de comisión.",
  },
  "/mediacion-familiar": {
    title: "Mediación familiar en Chile | Colega Link",
    description: "Solicita mediación familiar privada y encuentra mediadores familiares verificados para avanzar hacia acuerdos claros y colaborativos.",
  },
  "/abogados": {
    title: "Abogados verificados en Chile | Colega Link",
    description: "Directorio de abogados verificados disponibles para colaboración profesional y delegaciones jurídicas en Chile.",
  },
  "/app/mediadores": {
    title: "Mediadores familiares verificados en Chile | Colega Link",
    description: "Encuentra mediadores familiares verificados, revisa sus perfiles y solicita una mediación privada en Chile.",
  },
  "/blog": {
    title: "Blog jurídico: mediación, delegaciones y tecnología | Colega Link",
    description: "Guías y análisis para abogados, mediadores familiares y personas que buscan servicios jurídicos en Chile.",
  },
  "/delegar": {
    title: "Delegar gestiones jurídicas con respaldo | Colega Link",
    description: "Delega audiencias y diligencias con abogados verificados, condiciones claras y gestión documentada.",
  },
  "/inscripcion": {
    title: "Inscripción Plan Pionero Fundador | Colega Link",
    description: "Postula a uno de los cupos del Plan Pionero Fundador de Colega Link y accede a beneficios para abogados verificados.",
  },
  "/embajador-pionero": {
    title: "Reto Pionero | Colega Link",
    description: "Conoce el Reto Pionero de Colega Link, sus requisitos, beneficios y reglas para abogados fundadores.",
  },
  "/comunidad": {
    title: "Comunidad jurídica colaborativa | Colega Link",
    description: "Una comunidad para abogados y mediadores que buscan colaborar, compartir conocimiento y ampliar su alcance profesional.",
  },
  "/guia": {
    title: "Guía para usar Colega Link",
    description: "Aprende cómo registrarte, verificar tu perfil, publicar delegaciones y utilizar las herramientas de Colega Link.",
  },
  "/para-inversionistas": {
    title: "Colega Link para inversionistas",
    description: "Conoce la propuesta de Colega Link y su visión para modernizar la colaboración y los servicios jurídicos en Chile.",
  },
  "/terminos": {
    title: "Términos y condiciones | Colega Link",
    description: "Consulta los términos y condiciones de uso de la plataforma Colega Link.",
  },
};

function cleanPath(pathname) {
  const trimmed = pathname !== "/" ? pathname.replace(/\/+$/, "") : pathname;
  if (trimmed === "/pricing") return "/planes";
  if (trimmed === "/app/abogados") return "/abogados";
  return trimmed || "/";
}

function resolveSeo(pathname) {
  const canonicalPath = cleanPath(pathname);
  if (STATIC_SEO[canonicalPath]) {
    return { ...STATIC_SEO[canonicalPath], canonicalPath, indexable: true };
  }
  if (canonicalPath.startsWith("/blog/")) {
    return {
      title: "Artículo jurídico | Colega Link",
      description: "Contenido jurídico elaborado por Colega Link para abogados, mediadores familiares y personas en Chile.",
      canonicalPath,
      indexable: true,
    };
  }
  if (/^\/app\/mediadores\/[^/]+$/.test(canonicalPath)) {
    return {
      title: "Perfil de mediador familiar verificado | Colega Link",
      description: "Revisa el perfil profesional de un mediador familiar verificado en Colega Link.",
      canonicalPath,
      indexable: true,
    };
  }
  return {
    title: "Colega Link | Área privada",
    description: "Acceso a la plataforma Colega Link.",
    canonicalPath,
    indexable: false,
  };
}

function SeoManager() {
  const { pathname } = useLocation();

  useEffect(() => {
    const seo = resolveSeo(pathname);
    const canonicalUrl = SITE_URL + seo.canonicalPath;
    document.title = seo.title;

    const setMeta = (attribute, key, content) => {
      let element = document.querySelector("meta[" + attribute + '=\"' + key + '\"]');
      if (!element) {
        element = document.createElement("meta");
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
      }
      element.setAttribute("content", content);
    };

    setMeta("name", "description", seo.description);
    setMeta("name", "robots", seo.indexable ? "index,follow,max-image-preview:large" : "noindex,nofollow");
    setMeta("property", "og:type", "website");
    setMeta("property", "og:site_name", "Colega Link");
    setMeta("property", "og:locale", "es_CL");
    setMeta("property", "og:title", seo.title);
    setMeta("property", "og:description", seo.description);
    setMeta("property", "og:url", canonicalUrl);
    setMeta("property", "og:image", DEFAULT_IMAGE);
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", seo.title);
    setMeta("name", "twitter:description", seo.description);
    setMeta("name", "twitter:image", DEFAULT_IMAGE);

    const canonicals = document.querySelectorAll('link[rel="canonical"]');
    let canonical = canonicals[0];
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);
    canonicals.forEach((item, index) => {
      if (index > 0) item.remove();
    });
  }, [pathname]);

  return null;
}

function App() {
  useEffect(() => {
    const onLoad = () => {
      unregisterServiceWorkers()
        .catch((err) => console.warn("SW unregister failed:", err?.message));
    };
    window.addEventListener("load", onLoad);
    return () => window.removeEventListener("load", onLoad);
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <ConfigProvider>
        <BrowserRouter>
          <SeoManager />
          <Toaster position="top-right" theme="dark" richColors closeButton />
          <InstallPrompt />
          <WhatsAppFloating />
          <Routes>
            {/* Public conversion page (no layout — full-bleed for IG ads) */}
            <Route path="/inscripcion" element={<Inscripcion />} />
            <Route path="/delegar" element={<DelegarLanding />} />

            <Route element={<AppLayout />}>
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<RegisterChooser />} />
              <Route path="/registro" element={<RegisterChooser />} />
              <Route path="/registro/:tipo" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/olvide-mi-contrasena" element={<ForgotPassword />} />
              <Route path="/restablecer-contrasena" element={<ResetPassword />} />
              <Route path="/pricing" element={<Navigate to="/planes" replace />} />
              <Route path="/planes" element={<Pricing />} />
              <Route path="/blog/delegar-gestion-juridica-whatsapp-riesgos" element={<DelegarWhatsappArticle />} />
              <Route path="/blog/:slug" element={<EditorialArticle />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/*" element={<Blog />} />
              <Route path="/gracias/checklist-delegar-sin-improvisar" element={<ChecklistGracias />} />
              <Route path="/embajador-pionero" element={<EmbajadorPionero />} />
              <Route path="/guia" element={<Guia />} />
              <Route path="/comunidad" element={<Comunidad />} />
              <Route path="/mediacion-familiar" element={<MediacionFamiliar />} />
              <Route path="/para-inversionistas" element={<ParaInversionistas />} />

              <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
              <Route path="/app/delegaciones" element={<ProtectedRoute><Delegations /></ProtectedRoute>} />
              <Route path="/app/delegaciones/nueva" element={<ProtectedRoute><DelegationCreate /></ProtectedRoute>} />
              <Route path="/app/delegaciones/:id/editar" element={<ProtectedRoute><DelegationEdit /></ProtectedRoute>} />
              <Route path="/app/delegaciones/:id" element={<ProtectedRoute><DelegationDetail /></ProtectedRoute>} />
              <Route path="/app/asistente-ia" element={<ProtectedRoute><AsistenteIA /></ProtectedRoute>} />
              <Route path="/app/asistente-ia/mis-consultas" element={<ProtectedRoute><MisConsultasIA /></ProtectedRoute>} />
              <Route path="/app/biblioteca-juridica" element={<ProtectedRoute><BibliotecaJuridica /></ProtectedRoute>} />
              {/* Directorio de mediadores — accesible sin login (público) */}
              <Route path="/app/mediadores" element={<Mediators />} />
              <Route path="/app/mediadores/:id" element={<MediatorDetail />} />
              <Route path="/app/abogados/:id" element={<ProtectedRoute><LawyerDetail /></ProtectedRoute>} />
              <Route path="/abogados" element={<Lawyers />} />
              <Route path="/app/abogados" element={<Navigate to="/abogados" replace />} />
              <Route path="/app/mediaciones" element={<ProtectedRoute><Mediations /></ProtectedRoute>} />
              <Route path="/app/mediaciones/:id" element={<ProtectedRoute><MediationDetail /></ProtectedRoute>} />
              <Route path="/app/mis-mediaciones" element={<ProtectedRoute><MisMediaciones /></ProtectedRoute>} />
              <Route path="/app/mediacion-familiar/:id" element={<ProtectedRoute><MediationRequestDetail /></ProtectedRoute>} />
              <Route path="/app/perfil" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
              <Route path="/app/invitar" element={<ProtectedRoute><Invitar /></ProtectedRoute>} />
              <Route path="/app/membresia" element={<ProtectedRoute><Membership /></ProtectedRoute>} />
              <Route path="/app/membresia/checkout" element={<ProtectedRoute><MembershipCheckout /></ProtectedRoute>} />
              <Route path="/app/membresia/reservar" element={<ProtectedRoute><MembershipReserve /></ProtectedRoute>} />
              <Route path="/app/foro" element={<ProtectedRoute><QAList /></ProtectedRoute>} />
              <Route path="/app/foro/nueva" element={<ProtectedRoute><QANew /></ProtectedRoute>} />
              <Route path="/app/foro/:id" element={<ProtectedRoute><QADetail /></ProtectedRoute>} />
              <Route path="/terminos" element={<Terms />} />
              <Route path="/preview-sonidos" element={<PreviewSonidos />} />
              <Route path="/app/preview-sonidos" element={<PreviewSonidos />} />
              <Route path="/app/admin" element={<ProtectedRoute role="admin"><AdminVerifications /></ProtectedRoute>} />
              <Route path="/app/admin/verificaciones" element={<ProtectedRoute role="admin"><AdminVerifications /></ProtectedRoute>} />
              <Route path="/app/admin/crm" element={<ProtectedRoute role="admin"><AdminCRM /></ProtectedRoute>} />
              <Route path="/app/admin/auditoria" element={<ProtectedRoute role="admin"><AdminAudit /></ProtectedRoute>} />
              <Route path="/app/admin/contabilidad" element={<ProtectedRoute role="admin"><AdminAccounting /></ProtectedRoute>} />
              <Route path="/app/admin/mantenimiento" element={<ProtectedRoute role="admin"><AdminUsersMaintenance /></ProtectedRoute>} />
              <Route path="/app/admin/planes" element={<ProtectedRoute role="admin"><AdminPlanPayments /></ProtectedRoute>} />
              <Route path="/app/admin/transferencias" element={<ProtectedRoute role="admin"><AdminManualTransfers /></ProtectedRoute>} />
              <Route path="/app/admin/configuracion" element={<ProtectedRoute role="admin"><AdminBusinessConfig /></ProtectedRoute>} />
              <Route path="/app/admin/profesionales" element={<ProtectedRoute role="admin"><AdminGestionProfesionales /></ProtectedRoute>} />
              <Route path="/app/admin/mediaciones" element={<ProtectedRoute role="admin"><AdminConsolaMediaciones /></ProtectedRoute>} />
              <Route path="/app/admin/tarifas" element={<ProtectedRoute role="admin"><AdminMarketRates /></ProtectedRoute>} />
              <Route path="/app/admin/riesgo" element={<ProtectedRoute role="admin"><AdminRiskCases /></ProtectedRoute>} />
            </Route>
          </Routes>
        </BrowserRouter>
        </ConfigProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
